import React, { useState, useEffect } from 'react';
import { Play, Loader2, ChevronDown, ChevronUp, Info, Zap, AlertTriangle, SkipForward } from 'lucide-react';
import {
  Manifest, ManifestVariable, ManifestTeam,
  AIScenarioConfig, TeamReadinessAssessment, VariableEligibilityOverride,
  ReadinessTier, AIEligibility,
  READINESS_TIER_LABELS, READINESS_TIER_DESCRIPTIONS, READINESS_ACCELERATION,
} from '../datasim.types';

// ─── Readiness tier selector ───────────────────────────────────────────────────

const TIER_COLORS: Record<number, string> = {
  0: 'bg-gray-100 text-gray-600 border-gray-300',
  1: 'bg-blue-50 text-blue-700 border-blue-300',
  2: 'bg-indigo-50 text-indigo-700 border-indigo-300',
  3: 'bg-violet-50 text-violet-700 border-violet-300',
  4: 'bg-purple-50 text-purple-700 border-purple-300',
};

const ELIGIBILITY_COLORS: Record<AIEligibility, string> = {
  high:   'bg-green-50 text-green-700 border-green-300',
  medium: 'bg-amber-50 text-amber-700 border-amber-300',
  low:    'bg-gray-50 text-gray-500 border-gray-300',
  none:   'bg-red-50 text-red-600 border-red-200',
};

function accelerationRange(tier: ReadinessTier, eligibility: AIEligibility): string {
  const rates = READINESS_ACCELERATION[tier] as Record<string, [number, number] | 0>;
  const r = rates[eligibility];
  if (!r || r === 0) return '0%';
  if (typeof r === 'number') return `${Math.round(r * 100)}%`;
  return `${Math.round(r[0] * 100)}–${Math.round(r[1] * 100)}%`;
}

// ─── Default AI eligibility suggestion ────────────────────────────────────────

function suggestEligibility(v: ManifestVariable): AIEligibility {
  if (v.ai_eligibility) return v.ai_eligibility;
  if (v.type === 'external') return 'none';
  const name = v.name.toLowerCase();
  if (/document|draft|report|analys|review|code|generat|data|test/.test(name)) return 'high';
  if (/validat|configur|qa|train|compliance/.test(name)) return 'medium';
  if (/approval|sign.?off|stakeholder|workshop|align|regul/.test(name)) return 'none';
  return 'medium';
}

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  manifest: Manifest | null;
  aiConfig: AIScenarioConfig | null;
  onAIConfigChange: (cfg: AIScenarioConfig | null) => void;
  onRunSimulation: () => void;
  simulating: boolean;
  completeVarCount: number;
}

export default function DataSimScenarioBuilder({
  manifest, aiConfig, onAIConfigChange, onRunSimulation, simulating, completeVarCount
}: Props) {
  const [aiEnabled, setAIEnabled] = useState(aiConfig !== null);
  const [showEligibilityTable, setShowEligibilityTable] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const teams: ManifestTeam[] = manifest?.teams?.length
    ? manifest.teams
    : [{ id: 'default', name: manifest?.project_name || 'Project Team', type: 'internal' }];

  const variables = (manifest?.variables || []).filter(v =>
    v.distribution?.params?.optimistic != null
  );

  // Initialize AI config when enabled
  useEffect(() => {
    if (aiEnabled && !aiConfig) {
      const defaultConfig: AIScenarioConfig = {
        readiness_assessments: teams.map(t => ({
          team_id: t.id,
          team_name: t.name,
          readiness_tier: (t.ai_readiness_tier || 1) as ReadinessTier,
        })),
        variable_overrides: variables.map(v => ({
          variable_id: v.id,
          ai_eligibility: suggestEligibility(v),
        })),
        include_downside_risk: false,
        downside_probability: 0.08,
      };
      onAIConfigChange(defaultConfig);
    } else if (!aiEnabled) {
      onAIConfigChange(null);
    }
  }, [aiEnabled]);

  function updateTeamTier(teamId: string, tier: ReadinessTier) {
    if (!aiConfig) return;
    onAIConfigChange({
      ...aiConfig,
      readiness_assessments: aiConfig.readiness_assessments.map(r =>
        r.team_id === teamId ? { ...r, readiness_tier: tier } : r
      ),
    });
  }

  function updateVarEligibility(varId: string, eligibility: AIEligibility) {
    if (!aiConfig) return;
    const existing = aiConfig.variable_overrides.find(o => o.variable_id === varId);
    const updated = existing
      ? aiConfig.variable_overrides.map(o => o.variable_id === varId ? { ...o, ai_eligibility: eligibility } : o)
      : [...aiConfig.variable_overrides, { variable_id: varId, ai_eligibility: eligibility }];
    onAIConfigChange({ ...aiConfig, variable_overrides: updated });
  }

  function getVarEligibility(varId: string): AIEligibility {
    return aiConfig?.variable_overrides.find(o => o.variable_id === varId)?.ai_eligibility
      || suggestEligibility(variables.find(v => v.id === varId)!);
  }

  function getTeamTier(teamId: string): ReadinessTier {
    return aiConfig?.readiness_assessments.find(r => r.team_id === teamId)?.readiness_tier ?? 1;
  }

  // Eligibility stats for summary
  const eligibilityCounts = aiEnabled ? {
    high: variables.filter(v => getVarEligibility(v.id) === 'high').length,
    medium: variables.filter(v => getVarEligibility(v.id) === 'medium').length,
    low: variables.filter(v => getVarEligibility(v.id) === 'low').length,
    none: variables.filter(v => getVarEligibility(v.id) === 'none').length,
  } : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

      {/* ── LEFT: Scenario config ─────────────────────────────────────── */}
      <div className="lg:col-span-2 space-y-4">

        {/* AI Impact toggle */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-violet-600" />
                <h3 className="text-sm font-bold text-gray-800">AI Impact Scenario</h3>
                <span className="text-xs bg-violet-100 text-violet-600 px-2 py-0.5 rounded-full font-medium">Phase 4</span>
              </div>
              <p className="text-xs text-gray-500 max-w-lg">
                Model how AI adoption changes the project timeline. Runs alongside the base simulation —
                shows clients what's possible at each readiness tier. External dependencies and approval
                gates are never accelerated.
              </p>
            </div>
            <button
              onClick={() => setAIEnabled(!aiEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ml-4 ${aiEnabled ? 'bg-violet-600' : 'bg-gray-200'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${aiEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {aiEnabled && aiConfig && (
          <>
            {/* Team Readiness Panel */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-gray-100 bg-violet-50">
                <p className="text-sm font-bold text-gray-800">Team Readiness Assessment</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Set the AI maturity level for each team. This is about the CLIENT's teams, not TSG.
                </p>
              </div>
              <div className="divide-y divide-gray-50">
                {teams.map(team => {
                  const tier = getTeamTier(team.id);
                  return (
                    <div key={team.id} className="px-4 py-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{team.name}</p>
                          <p className="text-xs text-gray-400 capitalize">{team.type} · {team.size ? `${team.size} people` : 'size unknown'}</p>
                        </div>
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${TIER_COLORS[tier]}`}>
                          Tier {tier} — {READINESS_TIER_LABELS[tier]}
                        </span>
                      </div>
                      {/* Tier selector */}
                      <div className="flex gap-1.5 flex-wrap">
                        {([0, 1, 2, 3, 4] as ReadinessTier[]).map(t => (
                          <button
                            key={t}
                            onClick={() => updateTeamTier(team.id, t)}
                            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                              tier === t ? TIER_COLORS[t] + ' font-bold' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                            }`}
                          >
                            {t}: {READINESS_TIER_LABELS[t]}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-2 italic">{READINESS_TIER_DESCRIPTIONS[tier]}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Variable Eligibility Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <button
                className="w-full px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between"
                onClick={() => setShowEligibilityTable(!showEligibilityTable)}
              >
                <div>
                  <p className="text-sm font-bold text-gray-800 text-left">Variable Eligibility Review</p>
                  <p className="text-xs text-gray-500 text-left mt-0.5">Confirm which workstreams benefit from AI acceleration</p>
                </div>
                {showEligibilityTable ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>

              {showEligibilityTable && (
                <>
                  <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      <strong>Hard rule:</strong> External dependencies and regulatory approvals must be set to "None."
                      AI does not make the OCC respond faster or a vendor deliver sooner.
                    </p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500">
                      <div className="col-span-4">Variable</div>
                      <div className="col-span-2">Type</div>
                      <div className="col-span-4">AI Eligibility</div>
                      <div className="col-span-2 text-right">Acceleration</div>
                    </div>
                    {variables.map(v => {
                      const elig = getVarEligibility(v.id);
                      // Get average tier across all teams
                      const avgTier = Math.round(
                        aiConfig.readiness_assessments.reduce((s, r) => s + r.readiness_tier, 0) /
                        Math.max(aiConfig.readiness_assessments.length, 1)
                      ) as ReadinessTier;
                      const accel = accelerationRange(avgTier, elig);
                      return (
                        <div key={v.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center">
                          <div className="col-span-4">
                            <p className="text-xs font-medium text-gray-800 truncate">{v.name}</p>
                            {v.team_id && <p className="text-xs text-gray-400">{teams.find(t => t.id === v.team_id)?.name}</p>}
                          </div>
                          <div className="col-span-2">
                            <span className="text-xs text-gray-500 capitalize">{v.type}</span>
                          </div>
                          <div className="col-span-4 flex gap-1 flex-wrap">
                            {(['high', 'medium', 'low', 'none'] as AIEligibility[]).map(e => (
                              <button
                                key={e}
                                onClick={() => updateVarEligibility(v.id, e)}
                                className={`px-2 py-0.5 rounded border text-xs font-medium transition-all ${
                                  elig === e ? ELIGIBILITY_COLORS[e] : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300'
                                }`}
                              >
                                {e}
                              </button>
                            ))}
                          </div>
                          <div className="col-span-2 text-right">
                            <span className={`text-xs font-mono font-bold ${
                              elig === 'none' ? 'text-red-400' :
                              elig === 'high' ? 'text-green-600' :
                              elig === 'medium' ? 'text-amber-600' : 'text-gray-400'
                            }`}>
                              {elig === 'none' ? '—' : accel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Downside Risk */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <p className="text-sm font-semibold text-gray-800">Include AI Downside Risk</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Model the probability that AI-introduced errors or over-reliance add rework cycles.
                    Recommend including this if teams are at Tier 1–2 or any workstream has low error tolerance.
                  </p>
                  {aiConfig.include_downside_risk && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-600">Rework probability per eligible task</label>
                        <span className="text-xs font-bold text-amber-600">
                          {Math.round((aiConfig.downside_probability || 0.08) * 100)}%
                        </span>
                      </div>
                      <input
                        type="range" min="2" max="25" step="1"
                        value={Math.round((aiConfig.downside_probability || 0.08) * 100)}
                        onChange={e => onAIConfigChange({ ...aiConfig, downside_probability: parseInt(e.target.value) / 100 })}
                        className="w-full accent-amber-500"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>2% (low risk)</span><span>25% (high risk)</span>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => onAIConfigChange({ ...aiConfig, include_downside_risk: !aiConfig.include_downside_risk })}
                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${aiConfig.include_downside_risk ? 'bg-amber-500' : 'bg-gray-200'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${aiConfig.include_downside_risk ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── RIGHT: Summary + Run ──────────────────────────────────────── */}
      <div className="space-y-4">

        {/* Scenario summary */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Simulation Plan</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-xs text-gray-700">Base scenario — always run</span>
            </div>
            {aiEnabled && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-violet-500" />
                <span className="text-xs text-gray-700">AI Impact scenario — overlay</span>
              </div>
            )}
            {aiEnabled && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-300" />
                <span className="text-xs text-gray-700">Readiness sensitivity (Tier 0→4)</span>
              </div>
            )}
          </div>
          {aiEnabled && eligibilityCounts && (
            <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-1.5">
              {[
                { label: 'High', count: eligibilityCounts.high, color: 'text-green-600' },
                { label: 'Medium', count: eligibilityCounts.medium, color: 'text-amber-600' },
                { label: 'Low', count: eligibilityCounts.low, color: 'text-gray-500' },
                { label: 'None', count: eligibilityCounts.none, color: 'text-red-500' },
              ].map(s => (
                <div key={s.label} className="text-center bg-gray-50 rounded p-1.5">
                  <p className={`text-sm font-bold ${s.color}`}>{s.count}</p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Variable count */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Ready</p>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${completeVarCount >= 2 ? 'bg-green-500' : 'bg-amber-400'}`} />
            <span className="text-xs text-gray-600">{completeVarCount} variables · 10,000 iterations</span>
          </div>
        </div>

        {/* Run button */}
        <button
          onClick={onRunSimulation}
          disabled={simulating || completeVarCount < 2}
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-red-600 text-white rounded-xl font-semibold text-sm hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
        >
          {simulating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Running{aiEnabled ? ' Base + AI...' : '...'}</>
          ) : (
            <><Play className="w-4 h-4" /> Run Simulation{aiEnabled ? ' + AI Impact' : ''}</>
          )}
        </button>

        {/* Skip AI */}
        {aiEnabled && (
          <button
            onClick={() => { setAIEnabled(false); setTimeout(onRunSimulation, 100); }}
            disabled={simulating}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 text-gray-500 text-xs rounded-lg hover:bg-gray-50 transition-colors"
          >
            <SkipForward className="w-3.5 h-3.5" />
            Run base only (skip AI scenario)
          </button>
        )}
      </div>
    </div>
  );
}
